import { Eye, Headset, ListChecks, ShieldCheck, IndianRupee, Scale } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import CTASection from "../components/CTASection";
import { IMAGES } from "../lib/siteConfig";

const REASONS = [
  {
    icon: Eye,
    title: "Transparent Communication",
    desc: "You always know what is happening with your enquiry — clear guidance, honest expectations and straightforward answers at every stage.",
  },
  {
    icon: Headset,
    title: "Dedicated Customer Support",
    desc: "A responsive support team that answers your questions, follows up on your application and stays reachable when you need us.",
  },
  {
    icon: ListChecks,
    title: "Simple, Streamlined Process",
    desc: "We turn a complicated journey into clear steps — documentation checklists, application assistance and organised coordination.",
  },
  {
    icon: ShieldCheck,
    title: "Professional Assistance",
    desc: "A trained team that understands lender requirements and presents your application in the most complete, accurate manner.",
  },
  {
    icon: IndianRupee,
    title: "No Hidden Charges",
    desc: "Any applicable service charges are communicated upfront, in writing, before you decide to proceed. No surprises later.",
  },
  {
    icon: Scale,
    title: "Compliance-First Approach",
    desc: "We never promise guaranteed approval and never act as a lender. Every application goes through the institution's own eligibility checks.",
  },
];

export default function WhyChooseUs() {
  return (
    <main>
      <PageHero
        testid="why-choose-us-hero"
        eyebrow="Why Choose Us"
        titleLines={["The CREDNOVA", "Standard of Service"]}
        description="Trust is earned through clarity, consistency and care. Here is what every customer can expect when they work with us."
      />

      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-20 lg:pb-28" data-testid="why-choose-us-grid">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {REASONS.map((r, i) => (
            <Reveal key={r.title} delay={(i % 3) * 0.08}>
              <div className="card-gold p-8 h-full" data-testid={`why-reason-${i + 1}`}>
                <div className="w-12 h-12 rounded-xl border border-[rgba(212,175,55,0.3)] bg-[#0A0A0D] flex items-center justify-center mb-6">
                  <r.icon size={22} className="text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-lg font-semibold text-white mb-3">{r.title}</h2>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{r.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-8" data-testid="why-choose-us-banner">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden border border-[rgba(212,175,55,0.25)]">
            <img src={IMAGES.handshake} alt="Professional partnership" className="w-full h-[320px] sm:h-[400px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0D]/90 via-[#0A0A0D]/60 to-transparent flex items-center">
              <div className="p-8 sm:p-14 max-w-lg">
                <p className="caption-gold mb-4">Our Promise</p>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white leading-snug mb-4">
                  Guidance You Can Rely On, Without the Fine-Print Games
                </h2>
                <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed">
                  We measure our success by the clarity of your experience — not by promises no facilitator can honestly make.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <CTASection
        title="Experience the Difference Yourself"
        subtitle="Submit an enquiry and see how a transparent, guided loan assistance process should feel."
      />
    </main>
  );
}
